import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  PLATFORM_ID,
  untracked,
} from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { assets } from '../../../core/constants/assets.const';
import { ButtonModule } from 'primeng/button';
import {
  NavigationEnd,
  RouterModule,
  ActivatedRoute,
  Router,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxUiLoaderModule, NgxUiLoaderService } from 'ngx-ui-loader';
import { filter } from 'rxjs';
import { User } from '../../../models/auth';
import { enquiry, enquiryResponse } from '../../../models/enquiries';
import { EncryptionService } from '../../../services/encryption.service';
import { EnquiryService } from '../../../services/enquiries.service';
import { GeoLocationService } from '../../../services/geo-location-service.service';
import { SocketsService } from '../../../services/sockets.service';
import { EnquiryCardComponent } from '../enquiry-card/enquiry-card.component';
import { withLoader } from '../../../core/helpers/loader.operator';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { I18nService } from '../../../services/i18n.service';

dayjs.extend(isToday);

@Component({
  selector: 'logibids-driver-enquiry',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    RouterModule,
    InfiniteScrollModule,
    FormsModule,
    ButtonModule,
    NgxUiLoaderModule,
    EnquiryCardComponent,
  ],
  templateUrl: './driver-enquiry.component.html',
  styleUrl: './driver-enquiry.component.scss',
})
export class DriverEnquiryComponent implements OnInit, OnDestroy {
  // Services
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(I18nService);
  private readonly enquiryService = inject(EnquiryService);
  private readonly socketsService = inject(SocketsService);
  private readonly encryption = inject(EncryptionService);
  private readonly ngxLoader = inject(NgxUiLoaderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly locationService = inject(GeoLocationService);

  // Constants & Signals
  protected readonly assetsConst = assets;
  protected readonly search = signal('');
  protected readonly size = signal(10);
  protected readonly from = signal(1);
  protected readonly total = signal(0);
  protected readonly auctions = signal<enquiry[]>([]);
  protected readonly isBlinking = signal(false);
  protected readonly coords = this.locationService.currentCoords;

  // Local State
  protected userProfile: User | null = null;
  protected readonly currencyRate = signal('1');
  protected readonly auctionType = signal<string>('');
  protected readonly isTrackingRoute = signal(false);
  protected readonly genderPrefId = 2;

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  loading = signal(false);
  protected lastEnquiryData: any = null;
  protected lastEnquiryAllData: any = null;
  private transitioning = false;
  private transitioningAll = false;

  constructor() {
    this.initUserAndRouting();
    this.setupEffects();
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.startBlinkingInterval();
    }
  }

  private initUserAndRouting() {
    this.userProfile = this.encryption.decryptLocalStorage('profile');

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        this.isTrackingRoute.set(
          event.urlAfterRedirects.startsWith('/sp-bids/tracking/')
        );
      });

    this.route.data
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.auctionType.set(data['auctionType'] || '');
      });
  }

  private setupEffects() {
    effect(() => {
      const current = this.coords();
      if (current && current.latitude > 0 && current.longitude > 0) {
        this.socketsService.sendPosition({
          lat: current.latitude,
          long: current.longitude,
        });
      }
    });

    effect(
      () => {
        const data = this.enquiryService.driverEnquiry();
        if (data && data?.enquiry && data.showList) {
          if (data === this.lastEnquiryData) return;

          // If we are already transitioning, consume the event and proceed
          if (this.transitioning) {
            this.transitioning = false;
            this.executeEnquiryLogic(data);
            this.lastEnquiryData = data;
            return;
          }

          const otherData = untracked(() =>
            this.enquiryService.driverEnquiryAll()
          );
          const isOtherVisible = otherData?.showList ?? false;
          const isSelfVisible = !!this.lastEnquiryData;

          // If any card is visible, close everything first
          if (isOtherVisible || isSelfVisible) {
            this.transitioning = true;
            this.enquiryService.setDriverSOcketData({
              ...data,
              showList: false,
            });

            if (isOtherVisible && otherData) {
              this.enquiryService.setDriverSOcketDataAllEnquiry({
                ...otherData,
                showList: false,
              });
            }

            setTimeout(() => {
              this.enquiryService.setDriverSOcketData(data);
            }, 400);
            return;
          }

          // Initial Show
          this.executeEnquiryLogic(data);
          this.lastEnquiryData = data;
        } else {
          this.lastEnquiryData = null;
          this.transitioning = false; // Reset transitioning state
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const data = this.enquiryService.driverEnquiryAll();
        if (data && data.showList && data.all) {
          if (data === this.lastEnquiryAllData) return;

          if (this.transitioningAll) {
            this.transitioningAll = false;
            this.executeEnquiryAllLogic(data);
            this.lastEnquiryAllData = data;
            return;
          }

          const otherData = untracked(() =>
            this.enquiryService.driverEnquiry()
          );
          const isOtherVisible = otherData?.showList ?? false;
          const isSelfVisible = !!this.lastEnquiryAllData;

          if (isOtherVisible || isSelfVisible) {
            this.transitioningAll = true;
            this.enquiryService.setDriverSOcketDataAllEnquiry({
              ...data,
              showList: false,
            });

            if (isOtherVisible && otherData) {
              this.enquiryService.setDriverSOcketData({
                ...otherData,
                showList: false,
              });
            }

            setTimeout(() => {
              this.enquiryService.setDriverSOcketDataAllEnquiry(data);
            }, 400);
            return;
          }

          this.executeEnquiryAllLogic(data);
          this.lastEnquiryAllData = data;
        } else {
          this.lastEnquiryAllData = null;
          this.transitioningAll = false; // Reset transitioning state
        }
      },
      { allowSignalWrites: true }
    );
  }

  private executeEnquiryLogic(data: any) {
    // Verify data is still valid and meant to be shown
    const currentData = this.enquiryService.driverEnquiry();
    if (!currentData?.showList) return;

    this.enquiryService.setDriverEnquiryReady(false);
    this.loading.set(true);
    const enq = Array.isArray(data.enquiry) ? data.enquiry[0] : data.enquiry;
    if (enq?.id) {
      if (this.userProfile) {
        this.getEnquiryById(enq.id.toString());
      }
    }
  }

  private executeEnquiryAllLogic(data: any) {
    // Verify data is still valid and meant to be shown
    const currentData = this.enquiryService.driverEnquiryAll();
    if (!currentData?.showList) return;

    this.enquiryService.setDriverEnquiryReady(false);
    this.loading.set(true);
    this.from.set(1);
    this.auctions.set([]);
    this.getAuctions();
  }

  private startBlinkingInterval() {
    this.intervalId = setInterval(() => {
      this.isBlinking.set(true);
      setTimeout(() => this.isBlinking.set(false), 2710);
    }, 10000);
  }

  protected getAuctions() {
    if (!this.userProfile) return;

    let query = `from=${this.from()}&size=${this.size()}&schedule_type=SCHEDULE&status=RUNNING`;
    if (this.search()) query += `&search=${this.search()}`;
    this.enquiryService
      .getEnqueries(query)
      .pipe(
        withLoader(this.ngxLoader, 'driver-loader'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res: enquiryResponse) => {
        const newItems = res.list.map(item => this.transformToEnquiry(item));
        this.auctions.update(prev => [...prev, ...newItems]);
        this.total.set(res.total);
        this.from.update(f => f + 1);
        this.loading.set(false);
        this.enquiryService.setDriverEnquiryReady(true);
      });
  }

  private getEnquiryById(enquiryId: string) {
    this.auctions.set([]);
    this.enquiryService
      .getEnqueryById(enquiryId)
      .pipe(
        withLoader(this.ngxLoader, 'driver-loader'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res: enquiry) => {
        const item = this.transformToEnquiry(res);
        this.auctions.set([item]);
        this.loading.set(false);
        this.enquiryService.setDriverEnquiryReady(true);
        const showTime = this.userProfile?.default_region?.se_show_time ?? 30;
        const intervalMs = showTime * 1000;

        setTimeout(() => {
          this.enquiryService.setDriverSOcketData({
            enquiry: [],
            showList: false,
          });
        }, intervalMs);
      });
  }

  private transformToEnquiry(item: enquiry): enquiry {
    return new enquiry(
      {
        ...item,
        currencyRate: this.currencyRate(),
        userCurrency: this.userProfile?.currency_code || 'KWD',
        isActiveHeight: true,
        ispreferences: false,
      },
      this.translateService,
      this.genderPrefId
    );
  }

  protected onScrollingFinished() {
    if (this.auctions().length >= this.total()) return;
    this.getAuctions();
  }

  protected handleBidPlaced() {
    this.enquiryService.setDriverSOcketData({
      enquiry: [],
      showList: false,
    });
  }

  protected handleAuctionAccepted() {
    this.enquiryService.setDriverSOcketData({
      enquiry: [],
      showList: false,
    });
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
